import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import {
	AutoSizer,
	Grid,
	GridCellProps,
	GridCellRangeProps,
	ScrollParams,
	Size,
	defaultCellRangeRenderer
} from 'react-virtualized';
import classNames from 'classnames';

import TimeAxis from './components/time-axis/time-axis';
import { useTimelineScroll } from './hooks/use-timeline-scroll';
import HorizontalScrollbar from './components/horizontal-scrollbar/horizontal-scrollbar';
import CurrentTimeBar from './components/current-time-bar/current-time-bar';
import {
	ItemPosition,
	TimeLineClusterItem,
	TimelineItem,
	TimelineItemClick,
	TimelineOptions,
	TimelinePublicApi,
	TimelineRow,
	TooltipRenderFunction,
	TypeId
} from './types';
import { hourInMili, rowHeight, timeAxisHeight } from './consts';
import Controls from './components/controls/controls';
import GridRow, { EmptyRow } from './components/grid-row/grid-row';
import useZoom from './hooks/use-zoom';
import TimeLineItemTooltip from './components/timeline-item-tooltip/timeline-item-tooltip';
import useTimeAxis from './hooks/use-time-axis';
import GridLines from './components/grid-lines/gird-lines';

import styles from './bringg-timeline.module.scss';

const TIMELINE_GRID_CONTAINER = 'timeline-grid-container';

interface Props {
	className?: string;
	rows: TimelineRow[];
	options: TimelineOptions;
	tooltipRenderer?: TooltipRenderFunction;
	tooltipCriteria?: (item: TimelineItemClick, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => boolean;
	tooltipContainerSelector?: string;
	onClick?: (event: React.MouseEvent) => void;
	onItemClick?: (item: TimelineItemClick, event: React.MouseEvent<HTMLDivElement>) => void;
	selectedItemsIds?: Set<TypeId>;
}

const DEFAULT_EXTRA_ROWS = 3;
const extraScrollRows = (height: number, rowsLength = 0) => (rowsLength * rowHeight > height ? DEFAULT_EXTRA_ROWS : 0);
const calcRowsCount = (rows: TimelineRow[], height: number) =>
	rows && rows.length > 0 ? rows.length + extraScrollRows(height, rows?.length) : 0;

const BringgTimeline = forwardRef<TimelinePublicApi, Props>(
	(
		{
			rows,
			options,
			className,
			tooltipRenderer,
			tooltipCriteria,
			tooltipContainerSelector,
			onClick,
			onItemClick,
			selectedItemsIds: selectedItemsIdsProp
		},
		ref
	) => {
		const {
			minAxisTime: rangeStart,
			maxAxisTime: rangeEnd,
			keepLayoutOnDom = false,
			onScroll,
			syncScrollOnWheel,
			rowClassName,
			cluster
		} = options;
		const gridRef = useRef<Grid>(null);
		const gridContainerRef = useRef<HTMLDivElement>();
		const [gridInnerScrollRef, setGridInnerScrollRef] = useState<HTMLDivElement | null>(null);

		const [selectForTooltip, setSelectForTooltip] = useState<TimelineItem | TimeLineClusterItem>();
		const [selectedItemElement, setSelectedItemElement] = useState<HTMLElement | null>(null);
		const [viewWidth, setViewWidth] = useState<number>(0);
		const [_scrollTop, setScrollTop] = useState(false);

		useEffect(() => {
			gridContainerRef.current = document.getElementById(TIMELINE_GRID_CONTAINER) as HTMLDivElement;
		}, []);

		useEffect(() => {
			let observer: MutationObserver;
			if (viewWidth === 0) {
				setGridInnerScrollRef(null);
			}

			if (gridContainerRef.current && !gridInnerScrollRef) {
				observer = new MutationObserver(() => {
					const innerScrollElement = gridContainerRef.current?.querySelector(
						'.ReactVirtualized__Grid__innerScrollContainer'
					);
					if (innerScrollElement) {
						observer.disconnect();
						setGridInnerScrollRef(innerScrollElement as HTMLDivElement);
					}
				});

				observer.observe(gridContainerRef.current, { subtree: true, childList: true });
			}

			return () => {
				observer?.disconnect();
			};
		}, [gridContainerRef, gridInnerScrollRef, viewWidth]);

		const removeTooltip = useCallback(() => {
			setSelectForTooltip(undefined);
			setSelectedItemElement(null);
		}, []);

		const { syncVerticalScroll } = useTimelineScroll({
			scrollElement: gridInnerScrollRef,
			container: gridContainerRef.current,
			onWheelCallback: removeTooltip,
			gridRef: gridRef.current,
			syncScrollOnWheel,
			onScroll
		});

		const [rangeWidth, setRangeWidth] = useState<number>(0);

		const { zoom, zoomIn, zoomOut, fitRange } = useZoom({
			rows,
			viewWidth,
			rangeStart,
			rangeEnd,
			rangeWidth,
			setRangeWidth
		});

		// setting TimelinePublicApi
		useImperativeHandle(
			ref,
			() => {
				return {
					scrollTo: (scrollTop: number) => {
						syncVerticalScroll(scrollTop);
					},
					fitRange
				};
			},
			[viewWidth, rangeStart]
		);

		const { daysInRange, hoursValues } = useTimeAxis({
			rangeStart,
			rangeEnd,
			zoom,
			axisOptions: options.timeAxisOptions
		});

		const timeAxisParams = useMemo(
			() => ({
				viewWidth,
				rangeStart,
				rangeWidth,
				zoom,
				days: daysInRange,
				hours: hoursValues
			}),
			[viewWidth, rangeStart, rangeWidth, zoom, daysInRange, hoursValues]
		);

		const calcItemPosition = useCallback(
			(start: number, end: number): ItemPosition => {
				const left = ((start - rangeStart) / hourInMili) * zoom;
				const width = ((end - start) / hourInMili) * zoom;

				return { left, width };
			},
			[rangeStart, zoom]
		);

		const itemClick = (
			item: TimelineItemClick,
			itemElement: HTMLDivElement | null,
			event: React.MouseEvent<HTMLDivElement>
		) => {
			if (onItemClick) onItemClick(item, event);
			const criteriaFn = typeof tooltipCriteria === 'function' ? tooltipCriteria : () => true;

			if (criteriaFn(item, event)) {
				if (item.id === selectForTooltip?.id) {
					setSelectForTooltip(undefined);
				} else {
					setSelectForTooltip(item);
				}

				setSelectedItemElement(itemElement);
			}
		};

		const rowRenderer = ({ rowIndex, style, key }: GridCellProps) => {
			if ((viewWidth === 0 && keepLayoutOnDom) || !rows[rowIndex]) {
				return <EmptyRow key={key} style={style} />;
			}

			return (
				<GridRow
					key={key}
					row={rows[rowIndex]}
					className={classNames(rowClassName, rows[rowIndex].className)}
					style={style}
					calcItemPosition={calcItemPosition}
					onRowHover={options.onRowHover}
					onItemClick={itemClick}
					selectedItems={selectedItemsIdsProp}
					selectForTooltip={selectForTooltip}
					cluster={cluster}
				/>
			);
		};

		const handleScroll = (params: ScrollParams) => {
			setScrollTop(params.scrollTop > 0);

			if (!syncScrollOnWheel) {
				removeTooltip();
				onScroll?.(params.scrollTop);
			}
		};

		function cellRangeRenderer(props: GridCellRangeProps) {
			const children = defaultCellRangeRenderer(props);

			children.unshift(<GridLines key={'grid-lines'} hoursValues={hoursValues} />);
			children.push(<CurrentTimeBar key={'current-time-bar'} rangeStart={rangeStart} zoom={zoom} />);

			return children;
		}

		return (
			<>
				<div
					className={classNames(styles.timelineContainer, className)}
					data-test-id="bringg-timeline"
					onClick={(e: React.MouseEvent) => {
						onClick?.(e);
					}}
				>
					<TimeAxis params={timeAxisParams} onWheelCallback={removeTooltip} />
					<AutoSizer
						onResize={(size: Size) => {
							setViewWidth(size.width);
						}}
					>
						{({ width, height }) => (
							<Grid
								id={TIMELINE_GRID_CONTAINER}
								ref={gridRef}
								className={styles.grid}
								style={{ overflowX: 'hidden' }}
								width={keepLayoutOnDom && width === 0 ? 1 : width}
								height={height - timeAxisHeight}
								rowCount={calcRowsCount(rows, height)}
								rowHeight={rowHeight}
								cellRenderer={rowRenderer}
								columnCount={1}
								columnWidth={rangeWidth}
								onScroll={handleScroll}
								cellRangeRenderer={cellRangeRenderer}
							/>
						)}
					</AutoSizer>
					<HorizontalScrollbar params={{ viewWidth, rangeWidth }} />
					<Controls onZoomIn={zoomIn} onZoomOut={zoomOut} />
					<div className={classNames(styles.shadow, { [styles.scroll]: _scrollTop })} />
				</div>
				<TimeLineItemTooltip
					tooltipRender={tooltipRenderer}
					targetElement={selectedItemElement}
					item={selectForTooltip}
					removeTooltip={removeTooltip}
					shouldShowTooltipArrow={true}
					tooltipContainerSelector={tooltipContainerSelector}
				/>
			</>
		);
	}
);

export default BringgTimeline;
